import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

export interface Contact {
  id: number;
  name: string;
  phone: string;
  status: 'new' | 'processing' | 'invalid' | 'consultation';
  source: string | null;
  level: string | null;
  funnel?: { id: number; name: string; domain: string } | null;
  consultation?: { id: number } | null;
  created_at?: string;
  updated_at?: string;
}

export interface ContactsState {
  contacts: Contact[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  filters: {
    search: string;
    status: string;
  };
  newContactsCount: number;
}

const initialState: ContactsState = {
  contacts: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
  },
  filters: {
    search: '',
    status: 'all',
  },
  newContactsCount: 0,
};

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchContactsAsync = createAsyncThunk(
  'contacts/fetchContacts',
  async (params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  } = {}, { rejectWithValue, getState }) => {
    try {
      const { page = 1, limit = 50, search = '', status = 'all' } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) queryParams.append('search', search);
      if (status && status !== 'all') queryParams.append('status', status);

      const response = await axios.get(
        `${API_BASE_URL}/contacts?${queryParams.toString()}`,
        { headers: getAuthHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch contacts'));
    }
  }
);

export const fetchNewContactsCountAsync = createAsyncThunk(
  'contacts/fetchNewCount',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/contacts/new-count`,
        { headers: getAuthHeaders(getState) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch new contacts count'));
    }
  }
);

export const updateContactStatusAsync = createAsyncThunk(
  'contacts/updateStatus',
  async ({ id, status }: { id: number; status: Contact['status'] }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/contacts/${id}/status`,
        { status },
        { headers: getAuthHeaders(getState) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update status'));
    }
  }
);

export const deleteContactAsync = createAsyncThunk(
  'contacts/deleteContact',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/contacts/${id}`,
        { headers: getAuthHeaders(getState) }
      );
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete contact'));
    }
  }
);

const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<{
      search?: string;
      status?: string;
    }>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = { search: '', status: 'all' };
      state.pagination.currentPage = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContactsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContactsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload.contacts;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchContactsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchNewContactsCountAsync.fulfilled, (state, action) => {
        state.newContactsCount = action.payload.count;
      })
      .addCase(updateContactStatusAsync.fulfilled, (state, action) => {
        const index = state.contacts.findIndex(c => c.id === action.payload.contact.id);
        if (index !== -1) {
          state.contacts[index] = action.payload.contact;
        }
      })
      .addCase(deleteContactAsync.fulfilled, (state, action) => {
        state.contacts = state.contacts.filter(c => c.id !== action.payload);
      });
  },
});

export const { clearError, setFilters, clearFilters, setPage } = contactsSlice.actions;
export default contactsSlice.reducer;
